import { duelMatchOf, type Entity, type World } from '@nine/shared';
import { boutFloorPx, setFloorDrop, tierFloorPx } from './zoomFloor';

/**
 * DUEL-RING MF1: while a bout runs the camera keeps both fighters on screen. Since 10-11 (user: the camera always
 * follows the character, 9yin-like; lane CAMERA-PITCH) it stays centred on the player and only caps the zoom so the
 * foe, on whichever side, stays clear of the bars and the dock (the caller still respects `minZoomPx()`), eased.
 */

/** Rough size of a figure in art pixels: reach above the feet, below them, and each side. */
const UP = 80;
const DOWN = 8;
const SIDE = 34;
const EASE = 2.2;

export interface DuelFrame {
  /** 0 (player-centred) to 1 (both fighters framed), eased. */
  blend: number;
  /** The other fighter, or null with no bout. */
  foe: Entity | null;
}

/** Measured from the real UI (the fighter bars, the dock), refreshed a few times a second; estimates until then. */
let measured: { top: number; bottom: number } | null = null;
let stamp = 0;
/** Cached: the rects are read (a forced layout) only when the window or the HUD's compaction changed, or while still unmeasured (retried 5x/s). */
let keyOf = '';
const measure = (h: number): void => {
  if (typeof document === 'undefined') return;
  const key = `${h}|${window.innerWidth}|${document.body.className}`;
  const now = performance.now();
  if (key === keyOf && (measured || now - stamp < 200)) return;
  keyOf = key;
  stamp = now;
  let top = 0;
  for (const el of document.querySelectorAll('.duel-bar')) {
    const r = (el.parentElement ?? el).getBoundingClientRect();
    if (r.height > 0 && r.bottom < h * 0.5) top = Math.max(top, r.bottom);
  }
  let bottom = 0;
  const dock = document.getElementById('dock')?.getBoundingClientRect();
  if (dock && dock.height > 0) bottom = h - dock.top + (h >= 500 ? 36 : 6);
  measured = top > 0 && bottom > 0 ? { top: top + 4, bottom } : null;
};

/** The top bars and bottom dock kept clear, in CSS pixels: the real elements' rects, else an estimate. */
export const dockSpace = (h: number): { top: number; bottom: number } => {
  measure(h);
  return measured ?? { top: h < 500 ? 100 : 125, bottom: h < 500 ? 175 : 205 };
};

export class DuelFraming {
  private blend = 0;
  private foe: Entity | null = null;

  /** Per frame: eases the blend toward 1 in a bout (a foe exists), else toward 0. */
  update(world: World, dt: number, scale: number): DuelFrame {
    const m = duelMatchOf(world)?.current;
    const foe = m ? (world.get(m.foeId) ?? null) : null;
    if (foe) this.foe = foe;
    const target = foe ? 1 : 0;
    const k = 1 - Math.exp(-dt * EASE);
    this.blend += (target - this.blend) * k;
    if (Math.abs(target - this.blend) < 0.002) this.blend = target;
    if (!foe && this.blend === 0) this.foe = null;
    // The zoom floor is lowered for the bout (to a clean step only, zoomFloor.ts) and put back once the zoom has
    // eased back above it.
    setFloorDrop(this.blend > 0 || scale < tierFloorPx() - 1e-3 ? boutFloorPx() : null);
    return { blend: this.blend, foe: this.foe };
  }

  /**
   * The zoom scale (render steps, the renderer's `scale`) at which the foe `b` fits round the player `a` with the
   * view centred on the player, given their projected positions (art pixels) and the window in CSS pixels.
   */
  static fitScale(
    a: readonly [number, number],
    b: readonly [number, number],
    w: number,
    h: number,
    pixelSize: number,
  ): number {
    const { top, bottom } = dockSpace(h);
    const availW = w * 0.88;
    const availH = Math.max(80, h - top - bottom);
    // Centred on the player: the foe may be on either side, so the room is twice the gap each way.
    const extW = 2 * (Math.abs(a[0] - b[0]) + SIDE);
    const extH = 2 * Math.abs(a[1] - b[1]) + UP + DOWN;
    return Math.min(availW / extW, availH / extH) / pixelSize;
  }
}
